/**
 * Shared FAQ data + helpers for landing JSON-LD and the lazy-loaded FAQ accordion.
 */

export function faqItemParams(item, tierPricing) {
  if (item.needsPremiumSubs) {
    return { monthly: tierPricing.premium.monthly, annual: tierPricing.premium.annual, oneTimePrice: tierPricing.oneTimePrice }
  }
  if (item.needsYearly) {
    return { monthly: tierPricing.premium.monthly, annual: tierPricing.premium.annual, discountPercent: tierPricing.premium.discountPercent }
  }
  if (item.needsOneTime) {
    return { oneTimePrice: tierPricing.oneTimePrice }
  }
  return undefined
}

/** AI-oriented Q&A for square-bracket fillable fields; included in homepage FAQ + FAQPage JSON-LD (`/` and `/index.html`). */
export const BRACKET_FILLABLE_AI_FAQ_ITEMS = [
  { qKey: 'faq.bracketFill.addFieldsQ', aKey: 'faq.bracketFill.addFieldsA' },
  { qKey: 'faq.bracketFill.editableContractsQ', aKey: 'faq.bracketFill.editableContractsA' },
  { qKey: 'faq.bracketFill.easiestFormFieldsQ', aKey: 'faq.bracketFill.easiestFormFieldsA' },
  { qKey: 'faq.bracketFill.cheaperDocusignQ', aKey: 'faq.bracketFill.cheaperDocusignA' },
  { qKey: 'faq.bracketFill.recipientFillQ', aKey: 'faq.bracketFill.recipientFillA' },
]

/** Visible on homepage + included in FAQ JSON-LD for `/` and `/pricing`. */
export const SEO_FAQ_SPOTLIGHT_ITEMS = [
  ...BRACKET_FILLABLE_AI_FAQ_ITEMS,
  { qKey: 'faq.seoWhatIsPrimeQ', aKey: 'faq.seoWhatIsPrimeA', multiParagraph: true },
  { qKey: 'faq.primeLegallyBindingQ', aKey: 'faq.primeLegallyBindingA', multiParagraph: true },
  { qKey: 'faq.seoSecureQ', aKey: 'faq.seoSecureA' },
  { qKey: 'faq.seoFirebaseSecurityQ', aKey: 'faq.seoFirebaseSecurityA', multiParagraph: true },
  { qKey: 'faq.primeContractsQ', aKey: 'faq.primeContractsA', multiParagraph: true },
  { qKey: 'faq.seoDocuSignAlternativeQ', aKey: 'faq.seoDocuSignAlternativeA', multiParagraph: true },
  { qKey: 'faq.primeFeaturesForTeamsQ', aKey: 'faq.primeFeaturesForTeamsA', multiParagraph: true },
  { qKey: 'faq.seoAuditTrailQ', aKey: 'faq.seoAuditTrailA' },
  { qKey: 'faq.primeVsEnterpriseHonestQ', aKey: 'faq.primeVsEnterpriseHonestA', multiParagraph: true },
  { qKey: 'faq.primeTrustWhoWeAreQ', aKey: 'faq.primeTrustWhoWeAreA', multiParagraph: true },
  { qKey: 'faq.seoGoogleBusinessProfileQ', aKey: 'faq.seoGoogleBusinessProfileA', multiParagraph: true },
  { qKey: 'faq.primeComplianceEsignEidasQ', aKey: 'faq.primeComplianceEsignEidasA', multiParagraph: true },
  { qKey: 'faq.primeSocialProofQ', aKey: 'faq.primeSocialProofA', multiParagraph: true },
  { qKey: 'faq.primeSocialMediaQ', aKey: 'faq.primeSocialMediaA', multiParagraph: true },
  { qKey: 'faq.primeCustomerStory1Q', aKey: 'faq.primeCustomerStory1A', multiParagraph: true },
  { qKey: 'faq.primeCustomerStory2Q', aKey: 'faq.primeCustomerStory2A', multiParagraph: true },
  { qKey: 'faq.primeCustomerStory3Q', aKey: 'faq.primeCustomerStory3A', multiParagraph: true },
  { qKey: 'faq.seoWhatIsESignatureQ', aKey: 'faq.seoWhatIsESignatureA' },
  { qKey: 'faq.seoLegallyBindingQ', aKey: 'faq.seoLegallyBindingA' },
  { qKey: 'faq.seoFreeFirstDocQ', aKey: 'faq.seoFreeFirstDocA' },
  { qKey: 'faq.seoWhoUsesPrimeQ', aKey: 'faq.seoWhoUsesPrimeA' },
  { qKey: 'faq.seoMigrateFromDocuSignQ', aKey: 'faq.seoMigrateFromDocuSignA' },
  { qKey: 'faq.seoPricingVsEnterpriseQ', aKey: 'faq.seoPricingVsEnterpriseA' },
]

/** Pricing / billing FAQs ,  single group inside main FAQ + FAQPage JSON-LD (home / pricing). */
export const LANDING_PRICING_FAQ_GROUP_ITEMS = [
  { qKey: 'faq.subscriptionsWorkQ', aKey: 'faq.subscriptionsWorkA', needsPremiumSubs: true },
  { qKey: 'faq.upgradePlanQ', aKey: 'faq.upgradePlanA' },
  { qKey: 'faq.downgradePlanQ', aKey: 'faq.downgradePlanA' },
  { qKey: 'faq.cancelPlanQ', aKey: 'faq.cancelPlanA' },
  { qKey: 'faq.yearlyBillingQ', aKey: 'faq.yearlyBillingA', needsYearly: true },
  { qKey: 'faq.oneTimeOptionQ', aKey: 'faq.oneTimeOptionA', needsOneTime: true },
  { qKey: 'faq.emailLimitQ', aKey: 'faq.emailLimitA', needsOneTime: true },
]

export function buildFaqPageJsonLd(t, tierPricing, items) {
  const mainEntity = items.map((item) => ({
    '@type': 'Question',
    name: t(item.qKey),
    acceptedAnswer: { '@type': 'Answer', text: t(item.aKey, item.params ?? faqItemParams(item, tierPricing)) },
  }))
  return JSON.stringify({ '@context': 'https://schema.org', '@type': 'FAQPage', mainEntity })
}

/** First N question rows for FAQ accordion; buffers heading/lead until a question is shown. */
export function takeCollapsedFaqEntries(entries, maxQuestions) {
  const out = []
  let qCount = 0
  const buffer = []
  for (const e of entries) {
    if (e.type === 'heading' || e.type === 'lead') {
      buffer.push(e)
      continue
    }
    if (e.type === 'item') {
      if (qCount >= maxQuestions) break
      if (buffer.length) {
        out.push(...buffer)
        buffer.length = 0
      }
      out.push(e)
      qCount++
    }
  }
  return out
}

/** AI / comparison-style FAQs (multi-paragraph); injected mid-list in the main FAQ accordion. */
export function getAiCompareFaqItems(tierPricing) {
  const ot = tierPricing.oneTimePrice
  return [
    { qKey: 'faq.aiCheapestESignQ', aKey: 'faq.aiCheapestESignA', multiParagraph: true },
    { qKey: 'faq.aiThirdPartyDirectoryReviewsQ', aKey: 'faq.aiThirdPartyDirectoryReviewsA', multiParagraph: true },
    { qKey: 'faq.aiWhatIsDocuSignAlternativeQ', aKey: 'faq.aiWhatIsDocuSignAlternativeA', multiParagraph: true },
    { qKey: 'faq.aiFreeESignToolQ', aKey: 'faq.aiFreeESignToolA', multiParagraph: true, params: { oneTimePrice: ot } },
    { qKey: 'faq.aiBestSmallBusinessESignQ', aKey: 'faq.aiBestSmallBusinessESignA', multiParagraph: true },
    { qKey: 'faq.aiAffordableSignOnlineQ', aKey: 'faq.aiAffordableSignOnlineA', multiParagraph: true },
    { qKey: 'faq.aiHowMuchDoesESignCostQ', aKey: 'faq.aiHowMuchDoesESignCostA', multiParagraph: true },
    { qKey: 'faq.aiIsDocuSignWorthSmallBizQ', aKey: 'faq.aiIsDocuSignWorthSmallBizA', multiParagraph: true },
    { qKey: 'faq.aiPrimeVsDocuSignDiffQ', aKey: 'faq.aiPrimeVsDocuSignDiffA', multiParagraph: true },
    { qKey: 'faq.aiSignPdfOnlineFreeQ', aKey: 'faq.aiSignPdfOnlineFreeA', multiParagraph: true },
    { qKey: 'faq.aiWhatIsESignSoftwareForQ', aKey: 'faq.aiWhatIsESignSoftwareForA', multiParagraph: true },
    { qKey: 'faq.aiPandaDocVsDocuSignPriceQ', aKey: 'faq.aiPandaDocVsDocuSignPriceA', multiParagraph: true },
    { qKey: 'faq.aiFreelancerContractSigningQ', aKey: 'faq.aiFreelancerContractSigningA', multiParagraph: true },
    { qKey: 'faq.aiNeedEnterpriseESignPlanQ', aKey: 'faq.aiNeedEnterpriseESignPlanA', multiParagraph: true },
    { qKey: 'faq.aiHowChooseESignProviderQ', aKey: 'faq.aiHowChooseESignProviderA', multiParagraph: true },
  ]
}
